import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { copySong } from '../../services/songService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Modal component for copying a song to a different owner (user or community)
 */
const SongCopyModal = ({ show, onHide, song, onCopyComplete }) => {
  const { user, userCommunities } = useAuth();
  const { t } = useLanguage();

  const [targetDestination, setTargetDestination] = useState('user');
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens with a new song
  useEffect(() => {
    if (show && song) {
      setTargetDestination('user');
      setNewTitle(song.title);
      setError(null);
    }
  }, [show, song?.id]);

  // Handle copy
  const handleCopy = async () => {
    if (!song) return;

    setLoading(true);
    setError(null);

    try {
      let targetOwnerType, targetOwnerId;

      if (targetDestination === 'user') {
        targetOwnerType = 'user';
        targetOwnerId = user.id;
      } else {
        // Community destination: format is 'community-{id}'
        targetOwnerType = 'community';
        targetOwnerId = parseInt(targetDestination.split('-')[1]);
      }

      const copiedSong = await copySong(
        song.id,
        targetOwnerType,
        targetOwnerId,
        newTitle !== song.title ? newTitle : null
      );

      // Call the callback to refresh songs
      if (onCopyComplete) {
        onCopyComplete(copiedSong);
      }

      onHide();
    } catch (err) {
      setError(err.response?.data?.error || t('songbook.copyModal.error'));
    } finally {
      setLoading(false);
    }
  };

  // Filter communities where user can create songs
  const editableCommunities = (userCommunities || []).filter(community => {
    // User can create songs if they're admin or if community allows member edits
    return community.is_admin || community.allow_member_edits;
  });

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('songbook.copyModal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.copyModal.selectDestination')}</Form.Label>
            <Form.Select
              value={targetDestination}
              onChange={(e) => setTargetDestination(e.target.value)}
              disabled={loading}
            >
              <option value="user">{t('songbook.copyModal.mySongs')}</option>
              {editableCommunities.map(community => (
                <option key={community.id} value={`community-${community.id}`}>
                  {community.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('songbook.copyModal.newTitle')}</Form.Label>
            <Form.Control
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              disabled={loading}
              placeholder={song?.title}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={handleCopy} disabled={loading || !newTitle.trim()}>
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {t('songbook.copyModal.copying')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              {t('songbook.copyModal.copyButton')}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SongCopyModal;
